/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useReducer, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import PageLayout from '../../components/PageLayout';
import RiskForm from '../../components/bots/sectioned-builder/RiskForm';
import FiltersForm from '../../components/bots/sectioned-builder/FiltersForm';
import RuleBuilder from '../../components/bots/sectioned-builder/RuleBuilder';
import { TIMEFRAMES } from '../../lib/timeframes';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AIStrategyChat from '../../components/bots/sectioned-builder/AIStrategyChat.jsx';
import { APP_ACTION_SURFACES } from '../../access/surfaces';
import { useActionAccess } from '../../access/actionAccess';
import { buildGraphFromSpec } from '../../lib/sectioned/serialize';
import { graphToUiSections, toUiOperator } from '../../lib/sectioned/deserialize';

import {
  getBot,
  getBotVersion,
  getStrategyDraft,
  listBotVersions,
  createBacktestConfig,
  launchBacktestRun,
  createStrategyDraft,
  importBotVersionAsDraft,
  listStrategyDrafts,
  normalizeStrategyInput,
  patchStrategyDraft,
  publishStrategyDraft,
  validateStrategyDraft,
  // optional/fallback, if you have it wired:
  getBotVersionStrategyGraph,
} from '../../api/botsApi';

/* ----------------------- Minimal, resilient section schema ---------------------- */
/** We only gate buttons by Entry/Exit. Be lenient on clause shapes. */
const SectionRelaxed = z.object({
  op: z.enum(['AND', 'OR']).or(z.string()), // be lenient with incoming data
  clauses: z.array(z.any()),
});

/* --------------------------------- State & reducer ------------------------------ */
const initialState = {
  entry_long: { op: 'AND', clauses: [] },
  entry_short: { op: 'AND', clauses: [] },
  exit_long: { op: 'OR', clauses: [] },
  exit_short: { op: 'OR', clauses: [] },
  risk: {
    risk_pct: 1,
    sl: { type: 'atr', mult: 1 },
    tp: { type: 'r_multiple', r: 2 },
    default_rr: 3.0,
    max_open_positions: 1,
    daily_loss_pct: 3,
    cooldown_minutes: 30,
  },
  filters: {
  days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false },
  session: [{ start: '00:00:00', end: '23:59:59' }],
  },
};

// Helper: convert fractional risk to percent with rounding to avoid float artifacts
const toPercent = (frac) => (Number.isFinite(frac) ? Number((frac * 100).toFixed(6)) : undefined);

function reducer(state, action) {
  if (import.meta.env?.DEV) {
    const first = (section) => section?.clauses?.[0]?.lhs?.params?.length;
    console.log('[SB.reducer] action', action.type, {
      before_entry_long_len: first(state.entry_long),
      payload_first_len: first(action.payload || {}),
    });
  }
  switch (action.type) {
    case 'LOAD':
      return { ...state, ...action.payload };
    case 'UPDATE_ENTRY_LONG':
  return { ...state, entry_long: typeof action.payload === 'function' ? action.payload(state.entry_long) : action.payload };
    case 'UPDATE_ENTRY_SHORT':
  return { ...state, entry_short: typeof action.payload === 'function' ? action.payload(state.entry_short) : action.payload };
    case 'UPDATE_EXIT_LONG':
  return { ...state, exit_long: typeof action.payload === 'function' ? action.payload(state.exit_long) : action.payload };
    case 'UPDATE_EXIT_SHORT':
  return { ...state, exit_short: typeof action.payload === 'function' ? action.payload(state.exit_short) : action.payload };
    case 'UPDATE_RISK':
      return { ...state, risk: action.payload };
    case 'UPDATE_FILTERS':
      return { ...state, filters: action.payload };
    default:
      return state;
  }
}

/* ----------------------------- Normalizers & helpers ---------------------------- */
const asGroup = (s) => (s?.type === 'group' ? s : { type: 'group', op: s?.op ?? 'AND', clauses: s?.clauses ?? [] });

/**
 * Convert agent-format clause (indicator/operator/compare_indicator) to
 * builder-format clause (lhs/op/rhs). No-op if already in builder format.
 */
const normalizeClauseShape = (clause) => {
  if (!clause) return clause;
  // Already in builder format (has lhs)
  if (clause.lhs) return clause;
  // Agent format: { indicator, params, field, operator, compare_indicator, compare_params, compare_field }
  if (clause.indicator || clause.operator) {
    const lhs = {
      type: 'indicator',
      name: (clause.indicator || 'ema').toLowerCase(),
      params: clause.params || {},
    };
    if (clause.field && clause.field !== 'value') lhs.output = clause.field;
    const op = toUiOperator(clause.operator || clause.op || 'crosses_above');
    let rhs;
    if (clause.compare_indicator) {
      rhs = {
        type: 'indicator',
        name: (clause.compare_indicator || 'ema').toLowerCase(),
        params: clause.compare_params || {},
      };
      if (clause.compare_field && clause.compare_field !== 'value') rhs.output = clause.compare_field;
    } else if (clause.value !== undefined) {
      rhs = clause.value;
    } else {
      rhs = 0;
    }
    return { type: 'clause', lhs, op, rhs, id: clause.id };
  }
  return clause;
};

/** Ensure any bare clause objects get a type and correct shape for the UI. */
const addClauseTypes = (section) => {
  if (!section) return section;
  // Normalize 'logic' → 'op' (the agent uses "logic" but the builder uses "op")
  if (!section.op && section.logic) {
    section = { ...section, op: section.logic };
  }
  if (!Array.isArray(section.clauses)) return section;
  const normalizeNode = (node) => {
    if (!node) return node;
    // Normalize 'logic' → 'op' at node level too
    if (!node.op && node.logic) {
      node = { ...node, op: node.logic };
    }
    if (node.type === 'group' || ((node.op || node.logic) && Array.isArray(node.clauses))) {
      // group-ish
      const grp = node.type === 'group' ? node : { type: 'group', ...node };
      return { ...grp, clauses: (grp.clauses || []).map(normalizeNode) };
    }
    // Convert agent clause format → builder format
    const normalized = normalizeClauseShape(node);
    // Ensure type
    const clauseNode = normalized.type === 'clause' ? normalized : { type: 'clause', ...normalized };
    return { ...clauseNode, op: toUiOperator(clauseNode.op) };
  };
  return { ...section, clauses: section.clauses.map(normalizeNode) };
};

const operatorInversionMap = {
  crosses_above: 'crosses_below',
  crosses_below: 'crosses_above',
  greater_than: 'less_than',
  less_than: 'greater_than',
  greater_than_or_equal: 'less_than_or_equal',
  less_than_or_equal: 'greater_than_or_equal',
  // equals, not_equals are symmetrical
};

const invertSpec = (spec) => {
  const invertNode = (node) => {
    if (!node) return node;
    if (node.type === 'group') {
      return { ...node, clauses: node.clauses.map(invertNode) };
    }
    if (node.type === 'clause') {
      const invertedOp = operatorInversionMap[node.op];
      return invertedOp ? { ...node, op: invertedOp } : node;
    }
    return node;
  };
  return { ...spec, clauses: spec.clauses.map(invertNode) };
};

/** Extracts our state from either:
 *  - getBotVersion().strategy_params.sectioned_spec + filters (your pasted response), OR
 *  - getBotVersionStrategyGraph().strategy_graph.sectioned_spec + filters (older endpoint)
 */
const extractFromApi = (raw) => {
  const sp = raw?.strategy_params;
  const ss = sp?.sectioned_spec || raw?.strategy_graph?.sectioned_spec; // a bit more lenient

  // Helper to extract sections from a spec-like object
  const extractSections = (src, filterSrc) => {
    const hasNewShape = src.entry_long || src.entry_short || src.exit_long || src.exit_short;
    if (hasNewShape) {
      const risk = (src.risk ?? (sp?.risk)) ?? initialState.risk;
      const normRisk = { ...risk, risk_pct: toPercent(risk?.risk_pct) ?? initialState.risk.risk_pct };
      const rawFilters = filterSrc ?? src.filters;
      return {
        entry_long: addClauseTypes(src.entry_long || { op: 'AND', clauses: [] }),
        entry_short: addClauseTypes(src.entry_short || { op: 'AND', clauses: [] }),
        exit_long: addClauseTypes(src.exit_long || { op: 'OR', clauses: [] }),
        exit_short: addClauseTypes(src.exit_short || { op: 'OR', clauses: [] }),
        risk: normRisk,
        filters: normalizeFiltersToUi(rawFilters) ?? initialState.filters,
      };
    }
    // backward compatibility: map old entry/exit to long versions
    if (src.entry || src.exit) {
      const risk = (src.risk ?? (sp?.risk)) ?? initialState.risk;
      const normRisk = { ...risk, risk_pct: toPercent(risk?.risk_pct) ?? initialState.risk.risk_pct };
      const rawFilters = filterSrc ?? src.filters;
      return {
        ...initialState,
        entry_long: addClauseTypes(src.entry || { op: 'AND', clauses: [] }),
        exit_long: addClauseTypes(src.exit || { op: 'OR', clauses: [] }),
        risk: normRisk,
        filters: normalizeFiltersToUi(rawFilters) ?? initialState.filters,
      };
    }
    return null;
  };

  // 1) Try nested sectioned_spec first
  if (ss) {
    const result = extractSections(ss, sp?.filters ?? raw?.strategy_graph?.filters);
    if (result) return result;
  }

  // 2) Flat strategy_params: agent may put entry_long/exit_long directly in strategy_params
  if (sp && (sp.entry_long || sp.entry_short || sp.exit_long || sp.exit_short || sp.entry || sp.exit)) {
    const result = extractSections(sp, sp.filters);
    if (result) return result;
  }

  // 3) Root level: raw itself has entry_long etc (e.g. bare spec object)
  if (raw && (raw.entry_long || raw.entry_short || raw.exit_long || raw.exit_short || raw.entry || raw.exit)) {
    const result = extractSections(raw, raw.filters);
    if (result) return result;
  }

  return initialState;
};

// ------- Filters shape mappers -------
// UI shape (internal): { days: { mon..sun: boolean }, session: Array<{start,end}> | Array<[start,end]> }
// Backend (new) contract: { days: { mon..sun: boolean }, sessions: Array<{ start,end } | [start,end]> }
// Backend (legacy) shape still supported on load: { allowed_days_of_week: number[], allowed_sessions: Array<{ start,end }> }
const indexToDay = ['mon','tue','wed','thu','fri','sat','sun'];

function normalizeFiltersToUi(f) {
  if (!f) return undefined;
  // If already UI-ish
  if (f.days || f.session || f.sessions) {
    const days = {
      mon: !!f.days?.mon, tue: !!f.days?.tue, wed: !!f.days?.wed,
      thu: !!f.days?.thu, fri: !!f.days?.fri, sat: !!f.days?.sat, sun: !!f.days?.sun,
    };
    const srcSessions = Array.isArray(f.session) ? f.session : (Array.isArray(f.sessions) ? f.sessions : undefined);
    const session = Array.isArray(srcSessions)
      ? srcSessions.map((w) => Array.isArray(w) ? { start: w[0], end: w[1] } : { start: w?.start, end: w?.end })
      : initialState.filters.session;
    return { days, session };
  }
  // Backend shape
  const days = { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false };
  if (Array.isArray(f.allowed_days_of_week)) {
    f.allowed_days_of_week.forEach((i) => {
      const key = indexToDay[i];
      if (key) days[key] = true;
    });
  }
  const session = Array.isArray(f.allowed_sessions)
    ? f.allowed_sessions.map((s) => ({ start: s.start, end: s.end }))
    : initialState.filters.session;
  return { days, session };
}

function uiFiltersToBackend(f) {
  if (!f) return undefined;
  // Send according to new BE contract
  const days = {
    mon: !!f.days?.mon, tue: !!f.days?.tue, wed: !!f.days?.wed,
    thu: !!f.days?.thu, fri: !!f.days?.fri, sat: !!f.days?.sat, sun: !!f.days?.sun,
  };
  const sessions = (Array.isArray(f.session) ? f.session : []).map((w) =>
    Array.isArray(w)
      ? [ String(w[0] || '00:00'), String(w[1] || '24:00') ]
      : { start: String(w?.start || '00:00'), end: String(w?.end || '24:00') }
  );
  return { days, sessions };
}

/** Normalize AI generator response into our builder state shape. */
const extractFromGenResponse = (resp, prevState) => {
  try {
    const sp = resp?.strategy_params || resp?.config?.strategy_params || {};
    const ss = sp?.sectioned_spec || {};

    const next = {
      entry_long: prevState?.entry_long || { op: 'AND', clauses: [] },
      entry_short: prevState?.entry_short || { op: 'AND', clauses: [] },
      exit_long: prevState?.exit_long || { op: 'OR', clauses: [] },
      exit_short: prevState?.exit_short || { op: 'OR', clauses: [] },
      risk: prevState?.risk || initialState.risk,
      filters: prevState?.filters || initialState.filters,
    };

    // Prefer explicit four-section shape
    if (ss.entry_long || ss.entry_short || ss.exit_long || ss.exit_short) {
      if (ss.entry_long) next.entry_long = addClauseTypes(ss.entry_long);
      if (ss.entry_short) next.entry_short = addClauseTypes(ss.entry_short);
      if (ss.exit_long) next.exit_long = addClauseTypes(ss.exit_long);
      if (ss.exit_short) next.exit_short = addClauseTypes(ss.exit_short);
    } else if (ss.entry || ss.exit) {
      // Two-section fallback
      if (ss.entry) next.entry_long = addClauseTypes(ss.entry);
      if (ss.exit) next.exit_long = addClauseTypes(ss.exit);
    } else {
      // Minimal shape fallback — if user got a shortcut object under sectioned_spec.filters (like sample),
      // keep builder filters intact and attempt to map a typical MA cross into an entry_long clause for convenience.
      // This is best-effort and safe: we only add a clause if we detect a structure like { crosses_above: { fast_ma: {...}, slow_ma: {...} } }
      const f = ss?.filters;
      if (f?.crosses_above?.fast_ma && f?.crosses_above?.slow_ma) {
        const fast = f.crosses_above.fast_ma;
        const slow = f.crosses_above.slow_ma;
        const clause = {
          type: 'clause',
          lhs: { type: 'indicator', name: fast?.type || 'ema', params: { length: fast?.length ?? 9 } },
          op: 'crosses_above',
          rhs: { type: 'indicator', name: slow?.type || 'ema', params: { length: slow?.length ?? 21 } },
        };
        next.entry_long = addClauseTypes({ op: 'AND', clauses: [clause] });
      }
    }

    // Risk may be inside sectioned_spec or at strategy_params level; normalize fraction->percent for UI
    const pickRisk = ss.risk || sp.risk;
    if (pickRisk) {
      const r = pickRisk;
      next.risk = { ...r, risk_pct: toPercent(r?.risk_pct) ?? (prevState?.risk?.risk_pct ?? initialState.risk.risk_pct) };
    }

    // Only adopt filters if it fits our builder schema (days/session)
    const maybeFilters = sp?.filters;
    if (maybeFilters) {
      const uiF = normalizeFiltersToUi(maybeFilters);
      if (uiF) next.filters = uiF;
    }

    return next;
  } catch (e) {
    console.error('extractFromGenResponse failed', e);
    return prevState || initialState;
  }
};

/* ---------------------------------- Component ---------------------------------- */
const StrategyBuilderPage = () => {
  const { botId, versionId } = useParams();
  const strategyBuilderBacktestAccess = useActionAccess(APP_ACTION_SURFACES.strategyBuilderBacktest);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeTab, setActiveTab] = useState('entry');
  const [direction, setDirection] = useState('long'); // 'long' | 'short'
  const [versionName, setVersionName] = useState('');
  const [requireApproval, setRequireApproval] = useState(true);
  const [botName, setBotName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeframe, setTimeframe] = useState('M1');
  const [showImport, setShowImport] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [publishedVersionId, setPublishedVersionId] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isValidatingDraft, setIsValidatingDraft] = useState(false);
  const [isPublishingDraft, setIsPublishingDraft] = useState(false);

  // ---- Checkpoint system ----
  const [checkpoints, setCheckpoints] = useState([]);
  const checkpointCounterRef = useRef(0);

  const createCheckpoint = useCallback((label) => {
    checkpointCounterRef.current += 1;
    const id = checkpointCounterRef.current;
    setCheckpoints((prev) => [
      ...prev,
      {
        id,
        label,
        timestamp: Date.now(),
        snapshot: JSON.parse(JSON.stringify(state)),
      },
    ]);
  }, [state]);

  const restoreCheckpoint = useCallback((cpId) => {
    setCheckpoints((prev) => {
      const cp = prev.find((c) => c.id === cpId);
      if (cp?.snapshot) {
        dispatch({ type: 'LOAD', payload: cp.snapshot });
      }
      return prev;
    });
  }, []);

  const loadDraftIntoState = useCallback((draft, message) => {
    if (!draft) return;
    const spec = draft.normalized_spec || draft.sectioned_spec || {};
    const payload = extractFromApi({
      strategy_params: {
        sectioned_spec: spec,
        filters: spec.filters,
        risk: spec.risk,
      },
    });
    setDraftId(draft.id || draft.draft_id || null);
    setDraftStatus(draft.status || null);
    setValidationResult(draft.validation_result || null);
    setPublishedVersionId(draft.published_version || draft.published_version_id || null);
    setVersionName(draft.name || '');
    dispatch({ type: 'LOAD', payload });
    if (message) toast.info(message);
  }, []);

  const handleDraftChanged = useCallback(async (mutation) => {
    const data = mutation?.data || {};
    const draft = mutation?.draft || data.draft || (
      data.draft_id || data.id || data.sectioned_spec || data.normalized_spec
        ? data
        : null
    );
    const changedDraftId = (
      mutation?.draftId ||
      draft?.id ||
      draft?.draft_id ||
      data.draft_id ||
      data.id ||
      null
    );

    if (draft) {
      loadDraftIntoState(draft, 'Strategy draft updated by AI agent');
      return;
    }

    try {
      if (changedDraftId) {
        const latestDraft = await getStrategyDraft(changedDraftId);
        loadDraftIntoState(latestDraft, 'Strategy draft updated by AI agent');
        return;
      }

      if (botId) {
        const draftsResponse = await listStrategyDrafts(botId);
        const drafts = Array.isArray(draftsResponse) ? draftsResponse : draftsResponse?.results || [];
        const latest = drafts.find((item) => item.status !== 'published') || drafts[0];
        if (latest) {
          loadDraftIntoState(latest, 'Strategy draft updated by AI agent');
        }
      }
    } catch (err) {
      console.warn('handleDraftChanged: could not refetch draft', err);
    }
  }, [botId, loadDraftIntoState]);

  // Fetch bot name
  useEffect(() => {
    const fetchBotName = async () => {
      if (!botId) return;
      try {
        const bot = await getBot(botId);
        setBotName(bot?.name || '');
      } catch {
        // Non-critical — bot name is cosmetic
      }
    };
    fetchBotName();
  }, [botId]);

  // Legacy fallback for old AI tools that still create BotVersion records directly.
  const handleBotVersionChanged = useCallback(async (newVersionId) => {
    const loadVersion = (version, label) => {
      console.log(`[Bot Builder] ${label} — raw version data:`, JSON.stringify(version, null, 2));
      setVersionName(version?.version_name || '');
      const payload = extractFromApi(version);
      console.log('[Bot Builder] extractFromApi result:', JSON.stringify(payload, null, 2));
      const hasContent = payload.entry_long?.clauses?.length > 0 || payload.exit_long?.clauses?.length > 0;
      if (hasContent) {
        dispatch({ type: 'LOAD', payload });
        toast.info('Strategy updated by AI agent');
      } else {
        console.warn('[Bot Builder] extractFromApi returned empty sections, skipping LOAD');
        // Still update version name
        dispatch({ type: 'LOAD', payload });
        toast.info('Strategy structure updated by AI agent');
      }
    };

    try {
      // If we have a specific new version ID from the tool result, fetch that directly
      if (newVersionId) {
        const version = await getBotVersion(newVersionId);
        if (version) {
          loadVersion(version, `Fetched new version ${newVersionId}`);
          return;
        }
      }

      // Fetch all versions for this bot and pick the newest one
      if (botId) {
        const versions = await listBotVersions(botId);
        const list = Array.isArray(versions) ? versions : versions?.results || [];
        if (list.length > 0) {
          const sorted = [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const latest = sorted[0];
          loadVersion(latest, `Fetched latest version for bot ${botId}`);
          return;
        }
      }

      // Fallback: refetch current version
      if (versionId) {
        const version = await getBotVersion(versionId);
        loadVersion(version, `Refetched current version ${versionId}`);
      }
    } catch (err) {
      console.warn('handleBotVersionChanged: could not refetch', err);
    }
  }, [versionId, botId]);

  // Load version + strategy
  useEffect(() => {
    const load = async () => {
      if (!versionId) {
        if (!botId) return;
        try {
          const draftsResponse = await listStrategyDrafts(botId);
          const drafts = Array.isArray(draftsResponse) ? draftsResponse : draftsResponse?.results || [];
          const latest = drafts.find((draft) => draft.status !== 'published') || drafts[0];
          if (latest) {
            loadDraftIntoState(latest);
          }
        } catch (e) {
          console.warn('Failed to load latest strategy draft', e);
        }
        return;
      }
      try {
        const version = await getBotVersion(versionId);
        setVersionName(version?.version_name || '');
        setRequireApproval(version?.strategy_params?.require_approval ?? true);
        setPublishedVersionId(version?.id || versionId);
        try {
          const importedDraft = await importBotVersionAsDraft({
            bot_version_id: versionId,
            draft_name: version?.version_name || undefined,
          });
          loadDraftIntoState(importedDraft, 'Version imported as an editable draft');
          return;
        } catch (e) {
          console.warn('Could not import version as draft; loading version shape directly', e);
        }

        // Prefer the response you pasted; fall back to strategy_graph if available
        const payload = extractFromApi(version);
        // If your project has a separate strategy-graph endpoint, try it and prefer it when it returns data.
        if (typeof getBotVersionStrategyGraph === 'function') {
          try {
            const graph = await getBotVersionStrategyGraph(versionId);
            // Prefer new graph shape if present
            const ss = graph?.strategy_graph?.sectioned_spec;
            if (ss && (ss.sections || ss.nodes)) {
              const ui = graphToUiSections(ss);
              const rawRisk = ss.risk || initialState.risk;
              const normalizedRisk = { ...rawRisk, risk_pct: toPercent(rawRisk?.risk_pct) ?? initialState.risk.risk_pct };
              dispatch({ type: 'LOAD', payload: {
                entry_long: addClauseTypes(ui.entry_long),
                entry_short: addClauseTypes(ui.entry_short),
                exit_long: addClauseTypes(ui.exit_long),
                exit_short: addClauseTypes(ui.exit_short),
                risk: normalizedRisk,
                // Normalize backend filters (allowed_days_of_week, allowed_sessions) to UI shape
                filters: normalizeFiltersToUi(graph?.strategy_graph?.filters) ?? initialState.filters,
              }});
            } else {
              const fromGraph = extractFromApi(graph);
              const chosen = (fromGraph.entry?.clauses?.length || fromGraph.exit?.clauses?.length) ? fromGraph : payload;
              dispatch({ type: 'LOAD', payload: chosen });
            }
          } catch {
            dispatch({ type: 'LOAD', payload });
          }
        } else {
          dispatch({ type: 'LOAD', payload });
        }
  } catch {
        toast.error('Failed to load version');
      }
    };
    load();
  }, [botId, loadDraftIntoState, versionId]);

  const strategySpec = useMemo(
    () => ({
      entry_long: asGroup(addClauseTypes(state.entry_long)),
      entry_short: asGroup(addClauseTypes(state.entry_short)),
      exit_long: asGroup(addClauseTypes(state.exit_long)),
      exit_short: asGroup(addClauseTypes(state.exit_short)),
      risk: state.risk,
      filters: state.filters,
    }),
    [state]
  );

  const buildCurrentSectionedSpec = useCallback(() => {
    const sectionedGraph = buildGraphFromSpec({
      entry_long: strategySpec.entry_long,
      entry_short: strategySpec.entry_short,
      exit_long: strategySpec.exit_long,
      exit_short: strategySpec.exit_short,
      risk: strategySpec.risk,
      timeframe,
    });
    return {
      schema_version: 'sectioned_strategy.v1',
      ...sectionedGraph,
      filters: uiFiltersToBackend(strategySpec.filters),
    };
  }, [strategySpec, timeframe]);

  const buildDraftPayload = useCallback(() => {
    const sectionedSpec = buildCurrentSectionedSpec();
    return {
      name: versionName || 'Strategy draft',
      source: 'manual',
      sectioned_spec: sectionedSpec,
      graph_json: { sectioned_spec: sectionedSpec },
    };
  }, [buildCurrentSectionedSpec, versionName]);

  const sectionsValid = useMemo(() => {
    try {
      z.object({
        entry_long: SectionRelaxed,
        entry_short: SectionRelaxed,
        exit_long: SectionRelaxed,
        exit_short: SectionRelaxed,
      }).parse({
        entry_long: strategySpec.entry_long,
        entry_short: strategySpec.entry_short,
        exit_long: strategySpec.exit_long,
        exit_short: strategySpec.exit_short,
      });
      return true;
  } catch {
      return false;
    }
  }, [strategySpec]);

  const saveDraft = useCallback(async ({ silent = false } = {}) => {
    if (!sectionsValid) {
      toast.error('Please fix entry/exit first.');
      return null;
    }
    setIsSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const draft = draftId
        ? await patchStrategyDraft(draftId, payload)
        : await createStrategyDraft({ ...payload, bot_id: botId });
      setDraftId(draft?.id || draftId);
      setDraftStatus(draft?.status || null);
      setValidationResult(draft?.validation_result || null);
      setPublishedVersionId(draft?.published_version || null);
      if (!silent) toast.success('Draft saved');
      return draft;
    } catch (e) {
      console.error(e);
      toast.error('Failed to save draft');
      return null;
    } finally {
      setIsSavingDraft(false);
    }
  }, [botId, buildDraftPayload, draftId, sectionsValid]);

  // Import helpers
  const importJsonToState = (obj) => {
    try {
      // 1) Full API shape
      if (obj?.strategy_params || obj?.strategy_graph) {
        return extractFromApi(obj);
      }
      // 2) AI generator-like
      if (obj?.config?.strategy_params || obj?.strategy_params) {
        return extractFromGenResponse(obj, state);
      }
      // 3) Root has sectioned_spec (+ optional filters)
      if (obj?.sectioned_spec) {
        const shim = { strategy_params: { sectioned_spec: obj.sectioned_spec, filters: obj.filters } };
        return extractFromApi(shim);
      }
      // 4) Looks like a sectioned spec directly (entry_long/short/exit_long/short)
      if (obj?.entry_long || obj?.entry || obj?.exit) {
        const ss = obj;
        const risk = ss.risk || state.risk || initialState.risk;
        const filters = normalizeFiltersToUi(obj.filters) || state.filters || initialState.filters;
        const mapTwoToFour = (s) => ({
          entry_long: addClauseTypes(s.entry_long || s.entry || { op: 'AND', clauses: [] }),
          entry_short: addClauseTypes(s.entry_short || { op: 'AND', clauses: [] }),
          exit_long: addClauseTypes(s.exit_long || s.exit || { op: 'OR', clauses: [] }),
          exit_short: addClauseTypes(s.exit_short || { op: 'OR', clauses: [] }),
        });
        return { ...mapTwoToFour(ss), risk: { ...risk, risk_pct: toPercent(risk?.risk_pct) ?? risk.risk_pct }, filters };
      }
      // 5) Maybe a bare builder state dump
      if (obj?.risk || obj?.filters) {
        return {
          entry_long: addClauseTypes(obj.entry_long || { op: 'AND', clauses: [] }),
          entry_short: addClauseTypes(obj.entry_short || { op: 'AND', clauses: [] }),
          exit_long: addClauseTypes(obj.exit_long || { op: 'OR', clauses: [] }),
          exit_short: addClauseTypes(obj.exit_short || { op: 'OR', clauses: [] }),
          risk: { ...(obj.risk || initialState.risk), risk_pct: toPercent(obj?.risk?.risk_pct) ?? (obj?.risk?.risk_pct ?? initialState.risk.risk_pct) },
          filters: normalizeFiltersToUi(obj.filters) || initialState.filters,
        };
      }
    } catch (e) {
      console.error('Import detection failed', e);
    }
    throw new Error('Unsupported JSON shape. Expect a payload containing strategy_params.sectioned_spec, or a sectioned spec with entry/exit fields.');
  };

  const normalizeImportedJsonToState = async (parsed) => {
    try {
      const normalized = await normalizeStrategyInput(parsed);
      if (normalized?.normalized_spec) {
        return extractFromApi({
          strategy_params: {
            sectioned_spec: normalized.normalized_spec,
            filters: normalized.normalized_spec.filters,
            risk: normalized.normalized_spec.risk,
          },
        });
      }
    } catch (e) {
      console.warn('Backend normalization failed; falling back to local import parser', e);
    }
    return importJsonToState(parsed);
  };

  const handleImportFromText = async () => {
    if (!importJsonText?.trim()) return;
    setIsImporting(true);
    try {
      const parsed = JSON.parse(importJsonText);
      const payload = await normalizeImportedJsonToState(parsed);
      dispatch({ type: 'LOAD', payload });
      toast.success('JSON imported');
      setShowImport(false);
      setImportJsonText('');
    } catch (e) {
      console.error('Import failed', e);
      toast.error('Failed to import JSON');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    setIsImporting(true);
    reader.onload = async () => {
      try {
        const text = String(reader.result || '');
        const parsed = JSON.parse(text);
        const payload = await normalizeImportedJsonToState(parsed);
        dispatch({ type: 'LOAD', payload });
        toast.success('JSON file imported');
        setShowImport(false);
        setImportJsonText('');
      } catch (e) {
        console.error('File import failed', e);
        toast.error('Failed to import JSON file');
      } finally {
        setIsImporting(false);
      }
    };
    reader.onerror = () => {
      setIsImporting(false);
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleInvert = () => {
    const sourceSection = `${activeTab}_${direction === 'long' ? 'short' : 'long'}`;
    const targetSection = `${activeTab}_${direction}`;
    const sourceSpec = state[sourceSection];

    if (!sourceSpec || sourceSpec.clauses.length === 0) {
      toast.info(`Source (${sourceSection.replace('_', ' ')}) is empty. Nothing to invert.`);
      return;
    }

    const invertedSpec = invertSpec(sourceSpec);
    const actionType = `UPDATE_${targetSection.toUpperCase()}`;

    dispatch({ type: actionType, payload: invertedSpec });
    toast.success(`Inverted ${sourceSection.replace('_', ' ')} into ${targetSection.replace('_', ' ')}.`);
  };

  const handleSaveDraft = async () => {
    await saveDraft();
  };

  const handleValidateDraft = async () => {
    const draft = await saveDraft({ silent: true });
    if (!draft?.id) return;
    setIsValidatingDraft(true);
    try {
      const response = await validateStrategyDraft(draft.id);
      const result = response?.validation_result;
      setValidationResult(result || null);
      setDraftStatus(response?.draft?.status || draft.status || null);
      if (result?.valid) toast.success('Draft is valid');
      else toast.error('Draft has validation errors');
    } catch (e) {
      console.error(e);
      toast.error('Failed to validate draft');
    } finally {
      setIsValidatingDraft(false);
    }
  };

  const handlePublishDraft = async () => {
    const draft = await saveDraft({ silent: true });
    if (!draft?.id) return;
    setIsPublishingDraft(true);
    try {
      const targetVersionId = versionId || publishedVersionId || null;
      const response = await publishStrategyDraft(draft.id, {
        version_name: versionName || 'V1',
        notes: 'Created from strategy builder draft.',
        require_approval: requireApproval,
        target_version_id: targetVersionId || undefined,
      });
      setDraftStatus(response?.draft?.status || 'published');
      setValidationResult(response?.draft?.validation_result || validationResult);
      setPublishedVersionId(response?.version?.id || null);
      toast.success(targetVersionId ? 'Version updated' : 'Draft published as a new version');
    } catch (e) {
      console.error(e);
      if (e?.validation_result) {
        setValidationResult(e.validation_result);
        setDraftStatus('invalid');
      }
      toast.error('Failed to publish draft');
    } finally {
      setIsPublishingDraft(false);
    }
  };

  const handleRunBacktest = async () => {
    if (!strategyBuilderBacktestAccess.ensureAccess()) {
      return;
    }
    if (!sectionsValid) {
      toast.error('Please fix entry/exit first.');
      return;
    }
    try {
      const sectionedGraph = buildGraphFromSpec({
        entry_long: strategySpec.entry_long,
        entry_short: strategySpec.entry_short,
        exit_long: strategySpec.exit_long,
        exit_short: strategySpec.exit_short,
        risk: strategySpec.risk,
        timeframe,
      });
      // Create config (decoupled timeframe). Use new spec field.
      const cfgResp = await createBacktestConfig({
        name: `Strategy Config ${new Date().toISOString()}`,
        spec: { sectioned_graph: sectionedGraph, filters: uiFiltersToBackend(strategySpec.filters) },
        default_timeframe: timeframe,
      });
      const cfg = cfgResp?.data || cfgResp;
      const configId = cfg?.id || cfg?.config_id;
      if (!configId) throw new Error('Config create returned no id');
      // Launch run with explicit timeframe & date window
      const runResp = await launchBacktestRun({
        config_id: configId,
        timeframe,
        data_window: startDate && endDate ? { from: startDate, to: endDate } : undefined,
      });
      const runId = runResp?.id || runResp?.run_id;
      toast.success(`Backtest run started${runId ? ' (' + runId + ')' : ''}`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to start backtest');
    }
  };

  return (
    <PageLayout
      title="Sectioned Strategy Builder"
      backButton={{
        label: versionId ? 'Back to Version' : 'Back to Bot',
        to: versionId ? `/bots/${botId}/versions/${versionId}` : `/bots/${botId}`,
        preferHistory: false,
        hideLabelOnMobile: true,
      }}
    >
      <div className="space-y-6">
        {/* AI Strategy Chat */}
        <AIStrategyChat
          botId={botId}
          botName={botName}
          botVersionId={versionId}
          botDraftId={draftId}
          botDraftStatus={draftStatus}
          currentSpec={strategySpec}
          onApplyStrategy={(resp) => {
            try {
              console.log('[Bot Builder] onApplyStrategy received:', JSON.stringify(resp, null, 2));
              // Try extractFromGenResponse first, then extractFromApi as fallback
              let payload;
              try {
                payload = extractFromGenResponse(resp, state);
              } catch {
                payload = extractFromApi(resp);
              }
              // If neither extracted meaningful data, try treating resp as a raw spec
              const hasContent = payload.entry_long?.clauses?.length > 0 || payload.exit_long?.clauses?.length > 0;
              if (!hasContent) {
                const altPayload = extractFromApi(resp);
                const altHas = altPayload.entry_long?.clauses?.length > 0 || altPayload.exit_long?.clauses?.length > 0;
                if (altHas) payload = altPayload;
              }
              console.log('[Bot Builder] onApplyStrategy payload:', JSON.stringify(payload, null, 2));
              dispatch({ type: 'LOAD', payload });
            } catch (e) {
              console.error('Failed to apply strategy from chat', e);
              toast.error('Failed to apply strategy');
            }
          }}
          onCreateCheckpoint={createCheckpoint}
          checkpoints={checkpoints}
          onRestoreCheckpoint={restoreCheckpoint}
          onDraftChanged={handleDraftChanged}
          onBotVersionChanged={handleBotVersionChanged}
        />

        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-card border border-borderDefault rounded-lg">
          <div className="flex items-center gap-4">
            <label htmlFor="version-name" className="text-sm font-medium">
              Version Name
            </label>
            <Input
              id="version-name"
              type="text"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="V1"
              className="w-64"
            />
          </div>
          <div className="flex items-center gap-3">
            {/* Trade Approval Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-textSecondary whitespace-nowrap">Require Approval</span>
              <button
                onClick={() => setRequireApproval(v => !v)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                           ${requireApproval ? 'bg-primary' : 'bg-gray-600'} cursor-pointer`}
                title="When enabled, live trades from this bot require manual approval before execution"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
                             ${requireApproval ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
                />
              </button>
            </div>
            <Button variant="outline" onClick={() => setShowImport(v => !v)}>
              {showImport ? 'Close Import' : 'Load JSON'}
            </Button>
            <div className="text-xs text-textSecondary">
              Draft: {draftStatus || (draftId ? 'draft' : 'unsaved')}
              {publishedVersionId ? ` • Published ${publishedVersionId}` : ''}
            </div>
            <Button variant="outline" onClick={handleSaveDraft} disabled={!sectionsValid || isSavingDraft}>
              {isSavingDraft ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button variant="outline" onClick={handleValidateDraft} disabled={!sectionsValid || isSavingDraft || isValidatingDraft}>
              {isValidatingDraft ? 'Validating...' : 'Validate'}
            </Button>
            <Button onClick={handlePublishDraft} disabled={!sectionsValid || isSavingDraft || isPublishingDraft}>
              {isPublishingDraft ? 'Publishing...' : 'Publish Version'}
            </Button>
          </div>
        </div>

        {validationResult && !validationResult.valid && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm">
            <div className="font-medium text-red-600">Draft validation failed</div>
            <ul className="mt-2 list-disc pl-5 text-xs text-textSecondary">
              {(validationResult.errors || []).slice(0, 5).map((error, index) => (
                <li key={`${error.code || 'error'}-${index}`}>
                  {error.path ? `${error.path}: ` : ''}{error.message || error.code}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Import Panel */}
        {showImport && (
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Import from JSON</h3>
              {isImporting && <span className="text-xs text-textSecondary">Importing…</span>}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-xs text-textSecondary">Upload .json file</label>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e)=> handleImportFile(e.target.files && e.target.files[0])}
                  className="block text-sm"
                />
                <div className="text-[11px] text-textSecondary">Accepts exported payloads with strategy_params.sectioned_spec or a sectioned spec root.</div>
              </div>
              <div className="space-y-2">
                <label className="block text-xs text-textSecondary">Paste JSON</label>
                <textarea
                  className="w-full h-32 p-2 rounded-md border bg-backgroundSurface text-xs"
                  placeholder='{"strategy_params": {"sectioned_spec": { ... }}}'
                  value={importJsonText}
                  onChange={(e)=>setImportJsonText(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" onClick={handleImportFromText} disabled={isImporting || !importJsonText.trim()}>Load</Button>
                  <Button type="button" size="sm" variant="secondary" onClick={()=>setImportJsonText('')} disabled={isImporting}>Clear</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b">
          <Tab title="Entry" isActive={activeTab === 'entry'} onClick={() => setActiveTab('entry')} />
          <Tab title="Exit" isActive={activeTab === 'exit'} onClick={() => setActiveTab('exit')} />
          <Tab title="Risk" isActive={activeTab === 'risk'} onClick={() => setActiveTab('risk')} />
          <Tab title="Filters" isActive={activeTab === 'filters'} onClick={() => setActiveTab('filters')} />
        </div>

        {/* Direction Selector */}
        {(activeTab === 'entry' || activeTab === 'exit') && (
          <div className="pt-4 flex items-center gap-4">
            <Segmented
              value={direction}
              onChange={setDirection}
              options={[{ label: 'Long', value: 'long' }, { label: 'Short', value: 'short' }]}
            />
            <Button variant="outline" size="sm" onClick={handleInvert}>
              Copy & Invert from {direction === 'long' ? 'Short' : 'Long'}
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs text-textSecondary">Timeframe</label>
              <select
                className="h-8 px-2 rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
              >
                {TIMEFRAMES.map(tf => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
              <span className="text-[11px] text-textSecondary" title="Aligned by last closed bar of selected timeframe.">Aligned by last closed bar.</span>
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'entry' && direction === 'long' && (
          <RuleBuilder
            section="entry_long"
            spec={strategySpec.entry_long}
            onChange={(updater) => {
              dispatch({ type: 'UPDATE_ENTRY_LONG', payload: updater });
            }}
            baseTimeframe={timeframe}
          />
        )}
        {activeTab === 'entry' && direction === 'short' && (
          <RuleBuilder
            section="entry_short"
            spec={strategySpec.entry_short}
            onChange={(updater) => {
              dispatch({ type: 'UPDATE_ENTRY_SHORT', payload: updater });
            }}
            baseTimeframe={timeframe}
          />
        )}

        {activeTab === 'exit' && direction === 'long' && (
          <RuleBuilder
            section="exit_long"
            spec={strategySpec.exit_long}
            onChange={(updater) => {
              dispatch({ type: 'UPDATE_EXIT_LONG', payload: updater });
            }}
            baseTimeframe={timeframe}
          />
        )}
        {activeTab === 'exit' && direction === 'short' && (
          <RuleBuilder
            section="exit_short"
            spec={strategySpec.exit_short}
            onChange={(updater) => {
              dispatch({ type: 'UPDATE_EXIT_SHORT', payload: updater });
            }}
            baseTimeframe={timeframe}
          />
        )}

        {activeTab === 'risk' && (
          <div className="rounded-xl border bg-card p-4">
            <RiskForm value={state.risk} onChange={(next) => dispatch({ type: 'UPDATE_RISK', payload: next })} />
          </div>
        )}

        {activeTab === 'filters' && (
          <div className="rounded-xl border bg-card p-4">
            <FiltersForm value={state.filters} onChange={(next) => dispatch({ type: 'UPDATE_FILTERS', payload: next })} />
          </div>
        )}

        {/* Backtest controls */}
        <div className="rounded-xl border border-borderDefault bg-card p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-textSecondary">
                Start Date
              </label>
              <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-textSecondary">
                End Date
              </label>
              <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>

            <div className="sm:col-span-3 flex items-end gap-2">
              <Button
                onClick={handleRunBacktest}
                disabled={!sectionsValid || strategyBuilderBacktestAccess.disabled}
                title={strategyBuilderBacktestAccess.disabled ? strategyBuilderBacktestAccess.blockedMessage : undefined}
              >
                Run Backtest
              </Button>
              {/* Placeholder: future multi-timeframe batch launch using launchBacktestRunBatch */}
              {/* <Button variant="outline" onClick={()=>handleRunBatch(['M1','M5','M15'])}>Run Batch (M1/M5/M15)</Button> */}
            </div>
          </div>
        </div>
      </div>

    </PageLayout>
  );
};

const Tab = ({ title, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
      isActive ? 'border-b-2 border-primary text-primary' : 'text-textSecondary hover:bg-primary/10'
    }`}
  >
    <span>{title}</span>
    {/* Optional: validity indicator can be more complex now */}
    {/* <span className={`w-2 h-2 rounded-full ${isValid ? 'bg-green-500' : 'bg-red-500'}`} /> */}
  </button>
);

const Segmented = ({ value, onChange, options }) => (
  <div className="inline-flex items-center p-1 rounded-md bg-muted">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-3 py-1 text-sm rounded-md transition ${
          value === o.value ? 'bg-background text-textPrimary shadow-sm' : 'text-textSecondary hover:text-textPrimary'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default StrategyBuilderPage;
